import { useErrorReporter, useWindowMessage } from '#imports';
import {
    BoxRevealComplete,
    CardRevealComplete,
    Complete,
    PackRevealComplete,
    Setup,
    StashOrPass,
    StepBoxReveal,
    StepCardsReveal,
    StepGoldenTicketReveal,
    StepIdle,
    StepPackReveal,
    UpdateCard,
    UpdateGoldenTicket,
    UpdateStep,
} from '@fanatics/resn-api/instant-rips';
import { EventEmitter } from 'eventemitter3';
import { computed, defineComponent, inject, nextTick, provide, ref, shallowRef, watch } from 'vue';

import { gsap } from '@resn/gsap';

import { themeParams } from '~/components/gl/Foreground/data';
import { useParentSearchParams } from '~/components/useParentSearchParams';
import { BOX_DUR, CARDS_DUR, PACK_DUR } from '~/core/constants';
import { useColorPalette } from '~/providers/ColorPaletteProvider';
import { createLogger } from '~/utils/logger';

const logger = createLogger('instant-rips.fanatics');

const FanaticsKey = 'env';

export const useFanatics = () => {
    const api = inject(FanaticsKey);
    if (!api) {
        throw new Error('useFanatics must be used within a FanaticsProvider');
    }
    return api;
};

const FanaticsProvider = defineComponent({
    inheritAttrs: false,
    setup() {
        const events = new EventEmitter();
        const reportError = useErrorReporter();

        const { sendMessage } = useWindowMessage();

        const breakData = shallowRef({
            currentViewer: '',
            breakInfo: {
                id: '',
                currentStep: null,
                currentCardIndex: -1,
                box: {
                    pack: { cards: [] },
                },
                goldenTicket: null,
            },
            rerip: false,
        });

        const box = computed(() => breakData.value.breakInfo?.box);
        const pack = computed(() => box.value?.pack);
        const user = computed(() => breakData.value.currentViewer);
        const goldenTicket = computed(() => breakData.value.breakInfo?.goldenTicket);
        const { setColorPalette } = useColorPalette();

        const stepBase = ref(StepIdle);
        const step = computed(() => (ready.value ? stepBase.value : StepIdle));

        const cards = computed(() => pack.value?.cards || []);
        const cardIndex = ref(0);
        const cardCurrent = computed(() => cards.value[cardIndex.value]);

        const ready = ref(false);
        const sweat = ref(false);
        const rerip = ref(false);
        const fgDimmed = ref(false);
        const fgProps = shallowRef({});

        // ― app params
        const uiHeightRatio = ref(0);
        const sceneOffset = ref(0);

        // ― palette
        const checkIsGoldPalette = () => {
            if (!goldenTicket.value) return false;
            const { ticketType } = goldenTicket.value;
            const bool = ticketType == StashOrPass || step.value == StepGoldenTicketReveal;
            return bool;
        };

        useParentSearchParams((params) => {
            if (params.get('sceneOffset')) {
                const val = parseFloat(params.get('sceneOffset'));
                if (typeof val === 'number') sceneOffset.value = val;
            }
        });

        watch(step, (val) => {
            let delay = 0;
            let messageID = null;

            switch (val) {
                case StepBoxReveal:
                    delay = BOX_DUR;
                    messageID = BoxRevealComplete;
                    break;
                case StepPackReveal:
                    messageID = PackRevealComplete;
                    delay = PACK_DUR;
                    break;
                case StepCardsReveal:
                    delay = CARDS_DUR;
                    messageID = CardRevealComplete;
                    break;
            }

            if (messageID) gsap.delayedCall(delay, () => sendMessage(messageID));
        });

        const setup = (data) => {
            updateBreakData(data);
            nextTick(() => {
                setColorPalette(null);
                // events.emit(SETUP);
                ready.value = true;
            });
        };

        const complete = () => {
            events.emit(Complete);
        };

        const updateBreakData = (data) => {
            if (!data) return;

            // Defensive check: ensure breakInfo exists before accessing
            if (!data.breakInfo) {
                reportError(new Error('Missing breakInfo in data payload'), {
                    source: 'instant-rips/fanatics-provider',
                    kind: 'data-integrity',
                    reason: 'missing-breakinfo',
                    payload: data,
                });
                return;
            }

            breakData.value = data;

            const { currentStep } = data.breakInfo;

            requestAnimationFrame(() => {
                updateBreakStep(currentStep);
                events.emit(Setup, data);
            });

            return FanaticsProvider;
        };

        const updateBreakStep = (id) => {
            stepBase.value = id;
            events.emit(UpdateStep, id);

            return FanaticsProvider;
        };

        const updateBreakCard = (data = {}) => {
            const hasIndex = typeof data.currentCardIndex === 'number';
            if (!data || !hasIndex) {
                logger.warn('Card update payload incomplete', {
                    event: 'payload.incomplete',
                    source: 'FanaticsProvider',
                    indexMissing: !hasIndex,
                    dataMissing: !data,
                });
                reportError(new Error('Missing card index for update'), {
                    source: 'instant-rips/fanatics-provider',
                    kind: 'data-integrity',
                    reason: 'missing-card-index',
                    payload: data,
                });
                return;
            }

            const cardsList = cards.value;
            if (!Array.isArray(cardsList) || !cardsList.length) {
                reportError(new Error('No cards available for card update'), {
                    source: 'instant-rips/fanatics-provider',
                    kind: 'data-integrity',
                    reason: 'empty-cards-array',
                    payload: data,
                });
                return;
            }

            const { currentCardIndex } = data;

            const cardCurrent = cardsList[currentCardIndex];
            if (!cardCurrent) {
                reportError(new Error('Card index out of bounds for update'), {
                    source: 'instant-rips/fanatics-provider',
                    kind: 'data-integrity',
                    reason: 'card-missing',
                    payload: data,
                });
                return;
            }

            const cardCurrentData = cardCurrent.updateCardData;
            if (!cardCurrentData || typeof cardCurrentData !== 'object') {
                reportError(new Error('Missing updateCardData on target card'), {
                    source: 'instant-rips/fanatics-provider',
                    kind: 'data-integrity',
                    reason: 'missing-update-card-data',
                    payload: data,
                });
                return;
            }

            if (cardCurrentData.active !== undefined) cardIndex.value = currentCardIndex;

            // update all cards state based on their update datas
            const keysState = [
                'zoom',
                'rotation',
                'flip',
                'direction',
                'revealed',
                'revealProgress',
                'revealAngle',
                'interactive',
            ];
            // cards.forEach((card) => {
            cardsList.forEach((card) => {
                const cardData = card.updateCardData;
                if (!cardData || typeof cardData !== 'object') return;
                keysState.forEach((key) => {
                    if (cardData[key] !== undefined) card[key] = cardData[key];
                });
            });

            events.emit(UpdateCard, cardCurrent);

            return FanaticsProvider;
        };

        const updateBreakGoldenTicket = (data = {}) => {
            const hasGoldenTicket = data.goldenTicket && goldenTicket.value;
            if (!hasGoldenTicket) return;

            const keysState = ['revealed', 'active'];
            const ticketData = data.goldenTicket.updateGoldenTicketData;
            keysState.forEach((key) => {
                if (ticketData[key] !== undefined) goldenTicket.value[key] = ticketData[key];
            });

            events.emit(UpdateGoldenTicket, goldenTicket.value);

            return FanaticsProvider;
        };

        const FanaticsProvider = {
            breakData,

            box,
            pack,
            user,
            ready,
            step,

            cards,
            cardIndex,
            cardCurrent,

            goldenTicket,

            sweat,
            fgDimmed,
            fgProps,
            rerip,
            events,
            setup,

            updateBreakData,
            updateBreakStep,
            updateBreakCard,
            updateBreakGoldenTicket,

            uiHeightRatio,
            sceneOffset,
            complete,
        };

        provide(FanaticsKey, FanaticsProvider);

        return FanaticsProvider;
    },

    render() {
        if (this.$slots.default) {
            return this.$slots.default();
        }

        return null;
    },
});

export default FanaticsProvider;
